/**
 * Shared bridge: read the Schedule Planner's first-generation inputs
 * so other sidebar tools stay connected to the same problem / company / syllabus.
 */

function safeParse(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch (_) {
    return fallback;
  }
}

function softClip(text, n = 4000) {
  const t = String(text || "").replace(/\s+/g, " ").trim();
  if (!t) return "";
  return t.length > n ? `${t.slice(0, n)}…` : t;
}

function firstSubjectFromSyllabus(syllabus) {
  const text = String(syllabus || "");
  const m = text.match(/(?:subject|course)\s*[:\-]\s*([^\n,]{3,60})/i);
  if (m) return m[1].trim();
  const line = text
    .split(/\r?\n/)
    .map((l) => l.replace(/^[-–—*•\d\.\)\s]+/, "").trim())
    .find((l) => l.length >= 4 && l.length <= 48 && !/syllabus|unit|chapter/i.test(l));
  return line || "";
}

/**
 * @returns {{
 *  hasPlan: boolean,
 *  problem: string,
 *  syllabus: string,
 *  dsaSyllabus: string,
 *  systemDesign: string,
 *  questions: string,
 *  company: string,
 *  role: string,
 *  subject: string,
 *  topicHint: string,
 *  linkedPsId: string|null,
 *  sources: string[],
 * }}
 */
export function getPlannerContext() {
  const last = safeParse("ap_lastGenInputs", {}) || {};
  const text = safeParse("ap_textInputs", {}) || {};
  const team = safeParse("ap_teamMembers", []) || [];
  const plans = safeParse("ap_plans", {}) || {};

  const problem = softClip(last.problem || last.problemStatement || text.problem || "", 6000);
  const syllabus = softClip(last.syllabus || text.syllabus || "", 8000);
  const dsaSyllabus = softClip(last.dsaSyllabus || text.dsaSyllabus || "", 4000);
  const systemDesign = softClip(last.systemDesign || text.systemDesign || "", 4000);
  const questions = softClip(last.questions || text.questions || "", 4000);

  const memberCompany = Array.isArray(team)
    ? team.map((m) => m?.targetCompany || m?.dreamCompany || "").find(Boolean)
    : "";
  const company = softClip(
    last.targetCompany ||
      last.dreamCompany ||
      last.company ||
      memberCompany ||
      "",
    80
  );
  const role = softClip(last.role || team?.[0]?.role || "", 80);

  const subject =
    softClip(last.primarySubject || firstSubjectFromSyllabus(syllabus), 60) ||
    (dsaSyllabus ? "DSA / Programming" : "");

  const topicHint = softClip(
    last.focusTopic ||
      systemDesign.split(/\r?\n/).map((l) => l.trim()).find((l) => l.length > 6) ||
      (dsaSyllabus ? "Arrays & basics" : ""),
    80
  );

  const sources = [];
  if (problem) sources.push("problem");
  if (syllabus) sources.push("syllabus");
  if (dsaSyllabus) sources.push("dsa");
  if (systemDesign) sources.push("systemDesign");
  if (company) sources.push("company");
  if (questions) sources.push("questions");

  return {
    hasPlan: Boolean(plans?.daily || plans?.weekly || problem || syllabus),
    problem,
    syllabus,
    dsaSyllabus,
    systemDesign,
    questions,
    company,
    role,
    subject,
    topicHint,
    linkedPsId: localStorage.getItem("ap_linkedPsId") || null,
    sources,
  };
}

/** Small banner copy for pages that auto-fill from planner */
export function plannerContextBanner(ctx) {
  if (!ctx?.hasPlan && !ctx?.sources?.length) {
    return "No Schedule Planner inputs found yet — generate a plan first, or fill fields manually.";
  }
  const bits = [];
  if (ctx.company) bits.push(`Company: ${ctx.company}`);
  if (ctx.subject) bits.push(`Subject: ${ctx.subject}`);
  if (ctx.problem) bits.push("Problem linked");
  if (ctx.syllabus) bits.push("Syllabus linked");
  return bits.length
    ? `Using Schedule Planner inputs — ${bits.join(" · ")}`
    : "Using Schedule Planner context where available.";
}

export default getPlannerContext;
